import React, {useContext, useState} from 'react';
import { Button, Row, Table, Space, Typography, Tag, Tooltip } from 'antd';
import {
    EditOutlined,
    FolderOutlined,
    PrinterOutlined,
    SendOutlined,
    CheckCircleOutlined,
    SyncOutlined,
    ExclamationCircleOutlined,
    QuestionCircleOutlined
} from '@ant-design/icons';
import {UserContext} from "@context/UserContext";
import { downloadWordDocument } from './services/lessonPlanApi';

const { Text } = Typography;

const LessonPlanManagerTable = ({
                         lessonPlans,
                         selectedRowKeys,
                         rowSelection,
                         onArchive,
                         onEdit,
                         isLoading, // For the table
                         isEditLoading // For the edit buttons
                     }) => {

    const {user} = useContext(UserContext);
    const [printingUuid, setPrintingUuid] = useState(null);

    /**
     * Helper function to render a status tag based on generationStatus
     */
    const renderStatusTag = (status) => {
        switch (status) {
            case 'PENDING':
                return (
                    <Tooltip title="Generation in progress...">
                        <Tag icon={<SyncOutlined spin />} color="processing">
                            Pending
                        </Tag>
                    </Tooltip>
                );
            case 'COMPLETE':
                // No tag for complete, as it's the default/clean state.
                // You could return a green "Complete" tag if preferred:
                // return (
                //     <Tooltip title="Generation complete.">
                //         <Tag icon={<CheckCircleOutlined />} color="success">
                //             Complete
                //         </Tag>
                //     </Tooltip>
                // );
                return null;
            case 'FAILED':
                return (
                    <Tooltip title="Generation failed. Please try creating a new plan.">
                        <Tag icon={<ExclamationCircleOutlined />} color="error">
                            Failed
                        </Tag>
                    </Tooltip>
                );
            case 'NOT_FOUND':
            default:
                return (
                    <Tooltip title="Generation status is unknown.">
                        <Tag icon={<QuestionCircleOutlined />} color="default">
                            Unknown
                        </Tag>
                    </Tooltip>
                );
        }
    };

    /**
     * Handles the print button click.
     * Calls the API to download the Word document.
     */
    const handleExport = async (record) => {
        if (printingUuid) return; // Prevent multiple clicks
        setPrintingUuid(record.lessonPlanUuid);

        try {
            const { blob, filename } = await downloadWordDocument(record.key);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;

            document.body.appendChild(a);
            a.click();

            // Clean up the temporary link
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            console.error('Failed to download Word document:', error);
            notification.error({
                message: 'Print Failed',
                description: error.message || 'Could not generate the Word document.',
            });
        } finally {
            setPrintingUuid(null);
        }
    };

    const columns = [
        {
            title: 'Lesson Plan',
            dataIndex: 'contentName',
            key: 'contentName',
            render: (text, record) => (
                <Space orientation="vertical" size={0}>
                    <Space>
                        <Text strong>{text}</Text>
                        {renderStatusTag(record.generationStatus)}
                    </Space>
                    <Text type="secondary">{record.lessonName}</Text>
                </Space>
            )
        },
        { title: 'Year', dataIndex: 'yearLevel', key: 'yearLevel' },
        { title: 'Duration', dataIndex: 'timeframe', key: 'timeframe' },
        { title: 'Date Created', dataIndex: 'createDate', key: 'createDate', render: (text, record) => user.dateTimeFormat.format(record.createDate) },
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => {
                const isComplete = record.generationStatus === 'COMPLETE';

                return (
                    <Space orientation="vertical" size="small">
                        <Tooltip title={!isComplete ? "Cannot edit until generation is complete" : ""}>
                            <Button
                                type="link"
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => onEdit(record)}
                                disabled={isEditLoading || !isComplete} // Disable on edit load OR if not complete
                            >
                                Edit
                            </Button>
                        </Tooltip>
                        <Tooltip title={!isComplete ? "Cannot assign until generation is complete" : ""}>
                            <Button
                                type="link"
                                icon={<SendOutlined />}
                                size="small"
                                style={{ color: isComplete ? '#52c41a' : undefined }} // Use default disabled color
                                disabled={!isComplete}
                            >
                                Assign
                            </Button>
                        </Tooltip>
                        <Tooltip title={!isComplete ? "Cannot print until generation is complete" : ""}>
                            <Button
                                type="link"
                                icon={<PrinterOutlined />}
                                size="small"
                                disabled={!isComplete || printingUuid}
                                loading={isLoading}
                                onClick={() => handleExport(record)}
                            >
                                Export
                            </Button>
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <Row style={{ marginBottom: 16 }}>
                <Button
                    icon={<FolderOutlined />}
                    onClick={onArchive}
                    disabled={selectedRowKeys.length === 0 || isLoading} // Disable if loading
                >
                    Archive Selected ({selectedRowKeys.length})
                </Button>
            </Row>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={lessonPlans}
                pagination={{ pageSize: 5 }}
                loading={isLoading}
            />
        </>
    );
};

export default LessonPlanManagerTable;